import React from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const STORAGE_BASE = API_BASE.replace(/\/api$/, '');

const ServiceTable = ({ data, loading, onDelete, user }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-10 text-center rounded-lg border shadow-sm">
        <p className="text-gray-500">No service records found matching the criteria.</p>
      </div>
    );
  }

  const formatCurrency = (amt) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amt);
  };

  // Only sum the parts that are NG (is_good === false)
  const calculateTotal = (details) => {
    return details
      .filter((curr) => !curr.is_good)
      .reduce((acc, curr) => {
        const partPrice = curr.part_price || curr.bike_part?.prices || 0;
        return acc + partPrice + (curr.service_price || 0);
      }, 0);
  };

  const PdfIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tiket / Tanggal</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identitas Pelanggan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status &amp; Follow-up</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Items</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Estimasi Biaya</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice PDF</th>
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((service) => {
              const invoiceUrl = (service.sa_invoice_path || service.invoice_path)
                ? `${STORAGE_BASE}/storage/${service.sa_invoice_path || service.invoice_path}`
                : null;

              return (
                <tr key={service.id} className="hover:bg-gray-50 transition duration-150">
                  {/* Tiket / Tanggal */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">{service.ticket_number}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(service.service_date).toLocaleDateString('id-ID', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </div>
                  </td>

                  {/* Identitas Pelanggan */}
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{service.customer_name}</div>
                    <div className="text-xs text-gray-600">
                      Plat: <span className="font-semibold">{service.customer_vehicle_number}</span>
                    </div>
                    <div className="text-xs text-gray-500">{service.bike?.name} ({service.customer_vehicle_kms} km)</div>
                    <div className="text-xs text-gray-500">Mesin: {service.customer_engine_number}</div>
                    <div className="mt-1 text-[10px] text-indigo-600 font-medium uppercase tracking-tighter">
                      Mekanik: {service.mechanic?.fullname || service.fullname || '-'}
                    </div>
                  </td>

                  {/* Status & Follow-up */}
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      <div className="text-xs text-blue-600 font-bold uppercase tracking-tight">
                        {service.station?.name}
                      </div>

                      {/* Unified Status Badge */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                          service.customer_confirmation_status === 'confirmed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : service.customer_confirmation_status === 'customer-initiated'
                            ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                            : service.customer_confirmation_status === 'not-confirmed'
                            ? 'bg-rose-50 text-rose-700 border-rose-200'
                            : service.customer_confirmation_status === 'none'
                            ? 'bg-gray-50 text-gray-700 border-gray-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {
                            service.customer_confirmation_status === 'confirmed'
                              ? 'Sudah Di-followup'
                              : service.customer_confirmation_status === 'customer-initiated'
                              ? 'Datang Sendiri'
                              : service.customer_confirmation_status === 'not-confirmed'
                              ? 'Belum Di-followup'
                              : service.customer_confirmation_status === 'none' || !service.customer_confirmation_status
                              ? 'None'
                              : 'WA Terkirim'
                          }
                        </span>

                        {service.station_contact && (
                          <span className="text-[10px] text-gray-400 font-medium">
                            via {service.station_contact.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Detail */}
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-600 max-w-xs overflow-hidden text-ellipsis">
                      {service.service_details?.length} komponen dicek
                      <div className="mt-1 flex flex-wrap gap-1">
                        {service.service_details?.filter(d => !d.is_good).map((d, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            NG: {d.custom_part_name || d.bike_part?.spare_part?.name || d.master_spare_part?.part_name || d.part_number || 'Unknown Part'}
                          </span>
                        ))}
                      </div>
                    </div>
                  </td>

                  {/* Estimasi Biaya */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-bold text-blue-700">
                      {formatCurrency(calculateTotal(service.service_details || []))}
                    </div>
                  </td>

                  {/* Invoice PDF */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {invoiceUrl ? (
                      <a
                        href={invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Buka Invoice PDF"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-900 text-xs font-semibold transition duration-200"
                      >
                        <PdfIcon />
                        PDF
                      </a>
                    ) : (
                      <span
                        title="Invoice belum tersedia"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-400 text-xs font-medium cursor-default"
                      >
                        <PdfIcon />
                        —
                      </span>
                    )}
                  </td>

                  {/* Aksi (admin/superadmin only) */}
                  {(user?.role === 'admin' || user?.role === 'superadmin') && (
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => {
                          if (window.confirm('Apakah Anda yakin ingin menghapus data servis ini? Tindakan ini tidak dapat dibatalkan.')) {
                            onDelete(service.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 p-2 rounded-lg transition duration-200"
                        title="Hapus Servis"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceTable;
